import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { usePatients, PatientSearchSelect } from "@/components/PatientPicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";

function toDateInput(iso) {
  try { return new Date(iso).toISOString().slice(0, 10); } catch { return ""; }
}

export default function RecallModal({ open, onOpenChange, recall, onSaved }) {
  const patients = usePatients();
  const isEdit = !!recall?.id;
  const [form, setForm] = useState({ cliniko_id: "", patient_name: "", reason: "", due_date: "", status: "upcoming" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        cliniko_id: recall?.cliniko_id || "",
        patient_name: recall?.patient_name || "",
        reason: recall?.reason || "",
        due_date: recall?.due_date ? toDateInput(recall.due_date) : toDateInput(new Date().toISOString()),
        status: recall?.status || "upcoming",
      });
    }
  }, [open, recall]);

  const pickPatient = (cid) => {
    const p = patients.find((x) => x.cliniko_id === cid);
    setForm((f) => ({ ...f, cliniko_id: cid, patient_name: p?.name || f.patient_name }));
  };

  const save = async () => {
    if (!form.patient_name || !form.reason) { toast.error("Patient and reason are required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, due_date: new Date(form.due_date).toISOString() };
      if (isEdit) await api.put(`/recalls/${recall.id}`, payload);
      else await api.post("/recalls", payload);
      toast.success(isEdit ? "Recall updated" : "Recall created");
      onSaved?.();
      onOpenChange(false);
    } catch { toast.error("Could not save recall — retry"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-white" data-testid="recall-modal">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Edit recall" : "New recall"}</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">Cliniko recall (mock) — clinician-managed follow-up.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-500">Patient</Label>
            <div className="mt-1"><PatientSearchSelect value={form.cliniko_id} onChange={pickPatient} testid="recall-patient-select" placeholder="Search patient..." /></div>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Reason</Label>
            <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="recall-reason-input" className="mt-1" placeholder="e.g. 6-week post-op review" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-500">Due date</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="recall-due-input" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1" data-testid="recall-status-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="upcoming">Upcoming</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="recall-save-btn" className="bg-teal-600 hover:bg-teal-700">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? "Save changes" : "Create recall"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
